import { ApprovalRequest, OutputEmail, OutputEmailAttachment, Page, SignatureRequest, SignatureRequestStatus, Workflow, WorkflowAutomationItem, WorkflowOutput, WorkflowUser } from "./interface";

export type FinalBreadcrumbLabel = "Review" | "In Progress" | "Complete";

const IN_PROGRESS_STAGES = new Set<WorkflowAutomationItem["stage"]>([
  "REVIEW_APPROVED",
  "OUTPUT_POST",
  "OUTPUT_PDF",
  "SIGNATURES_DRAFT",
  "SIGNATURES_REQUEST",
  "SIGNATURES_PENDING",
  "SIGNATURES_COMPLETED",
  "EMAIL_DRAFT",
  "EMAIL",
]);

const TERMINAL_SIGNATURE_STATUSES = new Set<SignatureRequestStatus>([
  "COMPLETED",
  "CANCELED",
  "EXPIRED",
  "ERROR",
]);

export function workflowHasSignatureRequests(
  workflow?: Workflow | null,
  automationItem?: WorkflowAutomationItem | null,
): boolean {
  if (workflow?.signatureConfig)
    return true;
  if (workflow?.hasSignatureRequest)
    return true;
  if (automationItem?.workflow?.hasSignatureRequest)
    return true;
  if (workflow?.documents?.some((doc) => doc.isSignable))
    return true;
  return false;
}

export function getActiveSignatureRequests(
  signatureRequests: SignatureRequest[] | undefined,
): SignatureRequest[] {
  return (signatureRequests ?? []).filter((request) => request.status !== "PREVIEW");
}

function hasPendingSignatureRequests(
  signatureRequests: SignatureRequest[] | undefined,
): boolean {
  return getActiveSignatureRequests(signatureRequests).some(
    request => !TERMINAL_SIGNATURE_STATUSES.has(request.status),
  );
}

export function isInProgress(
  automationItem: WorkflowAutomationItem | undefined,
  workflow: Workflow,
): boolean {
  if (!automationItem)
    return false;

  // Status can be COMPLETED while signature requests are still open.
  if (hasPendingSignatureRequests(automationItem.signatureRequests))
    return true;

  if (automationItem.status?.startsWith("COMPLETED"))
    return false;

  if (IN_PROGRESS_STAGES.has(automationItem.stage))
    return true;

  const signatureManualSend = isSignatureManualSendEnabled(workflow);
  const emailManualSend = automationItem.outputEmails?.some(e => !isOutputEmailAutoSend(e)) ?? false;
  return !!getManualSendNote(automationItem.stage, signatureManualSend, emailManualSend);
}

export function getFinalBreadcrumbLabel(
  workflow: Workflow,
  currentPage: Page,
  automationItem?: WorkflowAutomationItem,
): FinalBreadcrumbLabel {
  if (currentPage === "DATA" || currentPage === "RECIPIENTS")
    return workflow.review ? "Review" : "Complete";

  if (workflow.review && (automationItem?.stage === "REVIEW_PENDING" || automationItem?.stage === "REVIEW_REJECTED"))
    return "Review";

  if (isInProgress(automationItem, workflow))
    return "In Progress";

  if (automationItem?.status?.startsWith("COMPLETED"))
    return "Complete";

  return "Complete";
}

export function getSignatureRequestRecipientLabel(request: SignatureRequest): string {
  const name = request.recipientName?.trim();
  if (name)
    return name;
  if (request.recipientEmail?.trim())
    return request.recipientEmail.trim();
  return `Recipient ${request.recipientNumber}`;
}

const SIGNATURE_STATUS_LABELS: Record<SignatureRequestStatus, string> = {
  PENDING: "Waiting...",
  REQUESTED: "Sent",
  ACCESSED: "Viewed",
  COMPLETED: "Signed",
  ERROR: "Error",
  CANCELED: "Canceled",
  EXPIRED: "Expired",
  PREVIEW: "Preview",
  LINK: "Link sent",
  LINK_UNPUBLISHED: "Unpublished",
};

export function isSignatureManualSendEnabled(workflow?: Workflow | null): boolean {
  return !!workflow?.signatureConfig?.enableDraftRequests;
}

export function isOutputEmailAutoSend(email: OutputEmail): boolean {
  return email.autoSend === true;
}

export function isOutputEmailSent(email: OutputEmail, sentEmailIds: Set<string>): boolean {
  return email.status === "SENT" || sentEmailIds.has(email.id);
}

export type EmailAutoSendState = "all" | "mixed" | "none";

export function getEmailAutoSendState(
  outputEmails: OutputEmail[],
): EmailAutoSendState | undefined {
  if (outputEmails.length === 0) return undefined;
  const flags = outputEmails.map(isOutputEmailAutoSend);
  if (flags.every(Boolean)) return "all";
  if (flags.some(Boolean)) return "mixed";
  return "none";
}

export interface OutputEmailAutoSendGroup {
  autoSend: boolean;
  emails: OutputEmail[];
}

/** One group when uniform; auto-send on/off groups when mixed. */
export function groupOutputEmailsByAutoSend(
  outputEmails: OutputEmail[],
): OutputEmailAutoSendGroup[] {
  if (outputEmails.length === 0) return [];

  const state = getEmailAutoSendState(outputEmails);
  if (state !== "mixed")
    return [{ autoSend: state === "all", emails: outputEmails }];

  const autoSendOn: OutputEmail[] = [];
  const autoSendOff: OutputEmail[] = [];
  for (const email of outputEmails) {
    if (isOutputEmailAutoSend(email)) autoSendOn.push(email);
    else autoSendOff.push(email);
  }

  const groups: OutputEmailAutoSendGroup[] = [];
  if (autoSendOn.length > 0) groups.push({ autoSend: true, emails: autoSendOn });
  if (autoSendOff.length > 0) groups.push({ autoSend: false, emails: autoSendOff });
  return groups;
}

export type ManualSendNote = "signature" | "email";

const SIGNATURE_MANUAL_SEND_STAGES = new Set([
  "SIGNATURES_DRAFT",
  "SIGNATURES_REQUEST",
  "SIGNATURES_PENDING",
]);

const EMAIL_MANUAL_SEND_STAGES = new Set([
  "EMAIL_DRAFT",
  "EMAIL",
  "SIGNATURES_COMPLETED",
]);

export function getManualSendNote(
  stage: string | undefined,
  signatureManualSend: boolean,
  emailManualSend: boolean,
): ManualSendNote | null {
  if (!stage)
    return null;

  if (signatureManualSend && SIGNATURE_MANUAL_SEND_STAGES.has(stage))
    return "signature";

  if (emailManualSend && EMAIL_MANUAL_SEND_STAGES.has(stage))
    return "email";

  return null;
}

export function isSignatureDraftPending(
  request: SignatureRequest,
  enableDraftRequests: boolean,
): boolean {
  return enableDraftRequests && request.status === "PENDING";
}

export function getSignatureRequestStatusLabel(
  status: SignatureRequestStatus,
  enableDraftRequests = false,
): string {
  if (status === "PENDING" && enableDraftRequests)
    return "Draft";
  return SIGNATURE_STATUS_LABELS[status] ?? status;
}

export type SignatureStatusPillStyle = "pending" | "active" | "success" | "error" | "neutral" | "draft";

export function getSignatureRequestStatusPillStyle(
  status: SignatureRequestStatus,
  enableDraftRequests = false,
): SignatureStatusPillStyle {
  if (status === "PENDING" && enableDraftRequests)
    return "draft";
  switch (status) {
    case "COMPLETED":
      return "success";
    case "ERROR":
    case "EXPIRED":
      return "error";
    case "CANCELED":
    case "LINK_UNPUBLISHED":
      return "neutral";
    case "REQUESTED":
    case "ACCESSED":
    case "LINK":
      return "active";
    default:
      return "pending";
  }
}

/** True when there is no distinct submitter to send a change request back to. */
export function isSoloReview(
  approvalRequests: ApprovalRequest[] | undefined,
  initiatingUser: WorkflowUser | null | undefined,
): boolean {
  const requests = approvalRequests ?? [];
  if (requests.length === 0)
    return true;

  const uniqueIds = new Set(requests.map(r => r.approver?.id).filter(Boolean));
  if (uniqueIds.size > 1)
    return false;

  const approverId = requests[0].approver?.id;
  const initiatorId = initiatingUser?.id;
  if (!approverId || !initiatorId)
    return true;

  return approverId === initiatorId;
}

export type PrimaryCtaKey =
  | "send_email"
  | "approve_and_send"
  | "approve_all"
  | "approve_document"
  | "approve_documents";

export type SuccessMessageKey = PrimaryCtaKey;

export interface RelevantUnseen {
  hasUnseenDocuments: boolean;
  hasUnseenEmails: boolean;
}

export const PRIMARY_CTA_LABELS: Record<Exclude<PrimaryCtaKey, "approve_documents" | "approve_document">, string> = {
  send_email: "Send email",
  approve_and_send: "Approve & Send",
  approve_all: "Approve All",
};

export function getPrimaryCtaLabel(ctaKey: PrimaryCtaKey, documentCount: number): string {
  if (ctaKey === "approve_document" || ctaKey === "approve_documents")
    return documentCount > 1 ? "Approve Documents" : "Approve Document";

  return PRIMARY_CTA_LABELS[ctaKey];
}

function extractAttachmentLinkId(attachment: OutputEmailAttachment): string | undefined {
  const raw = attachment as OutputEmailAttachment & Record<string, unknown>;

  const candidates: unknown[] = [
    attachment.output,
    attachment.document,
    raw.workflowDocument,
    raw.outputId,
    raw.documentId,
    raw.workflow_document,
    raw.output_id,
    raw.document_id,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate)
      return candidate;
    if (candidate && typeof candidate === "object" && "id" in candidate) {
      const id = (candidate as { id: unknown }).id;
      if (typeof id === "string" && id)
        return id;
    }
  }

  return undefined;
}

function findOutputForAttachment(
  attachment: OutputEmailAttachment,
  outputs: WorkflowOutput[],
): WorkflowOutput | undefined {
  const linkId = extractAttachmentLinkId(attachment);
  if (linkId) {
    const byOutputId = outputs.find(o => o.id === linkId);
    if (byOutputId) return byOutputId;

    const byDocumentId = outputs.find(o => o.document === linkId);
    if (byDocumentId) return byDocumentId;
  }

  const byFileName = outputs.find(o =>
    o.file?.name === attachment.attachmentName ||
    o.filePdf?.name === attachment.attachmentName ||
    o.onedriveFile?.fileName === attachment.attachmentName ||
    o.hubspotFilePdf?.fileName === attachment.attachmentName,
  );
  if (byFileName) return byFileName;

  return undefined;
}

function getDistinctOutputsByDocument(outputs: WorkflowOutput[]): WorkflowOutput[] {
  const sorted = [...outputs].sort((a, b) => a.automationIndex - b.automationIndex);
  const seen = new Set<string>();
  const distinct: WorkflowOutput[] = [];

  for (const output of sorted) {
    if (output.document) {
      if (seen.has(output.document)) continue;
      seen.add(output.document);
    }
    distinct.push(output);
  }

  return distinct;
}

export function getAttachmentDisplayName(
  attachment: OutputEmailAttachment,
  outputs: WorkflowOutput[],
  attachmentIndex = 0,
): string {
  const matched = findOutputForAttachment(attachment, outputs);
  if (matched?.outputName)
    return matched.outputName;

  const distinctOutputs = getDistinctOutputsByDocument(outputs);
  const outputByIndex = distinctOutputs[attachmentIndex];
  if (outputByIndex?.outputName)
    return outputByIndex.outputName;

  if (distinctOutputs.length === 1 && distinctOutputs[0].outputName)
    return distinctOutputs[0].outputName;

  return attachment.attachmentName;
}

/** Count distinct workflow documents, not output file variants (PDF + Google = one doc). */
export function getDocumentCount(outputs: WorkflowOutput[]): number {
  if (outputs.length === 0)
    return 0;

  const docIds = outputs.map(o => o.document).filter(Boolean);
  if (docIds.length === outputs.length)
    return new Set(docIds).size;

  return outputs.length;
}

export function getPrimaryCtaKey(
  outputs: WorkflowOutput[],
  outputEmails: OutputEmail[],
): PrimaryCtaKey {
  const documentCount = getDocumentCount(outputs);
  const hasEmails = outputEmails.length > 0;
  const hasDocs = documentCount > 0;
  const autoSendState = getEmailAutoSendState(outputEmails);

  if (!hasDocs && hasEmails)
    return "send_email";

  if (autoSendState === "all")
    return "approve_and_send";

  if (hasEmails && autoSendState !== undefined)
    return "approve_documents";

  if (documentCount > 1)
    return "approve_all";

  return "approve_document";
}

export function getRelevantUnseen(
  outputs: WorkflowOutput[],
  outputEmails: OutputEmail[],
  seenItems: Set<string>,
): RelevantUnseen {
  const documentCount = getDocumentCount(outputs);

  const hasUnseenDocuments =
    documentCount > 1 && outputs.some(o => !seenItems.has(o.id));

  const hasUnseenEmails = outputEmails.some(e => !seenItems.has(e.id));

  return { hasUnseenDocuments, hasUnseenEmails };
}

export function hasRelevantUnseen(unseen: RelevantUnseen): boolean {
  return unseen.hasUnseenDocuments || unseen.hasUnseenEmails;
}

export type UnseenConfirmKey =
  | "documents_and_email"
  | "documents_only"
  | "email_only";

export function getUnseenConfirmKey(unseen: RelevantUnseen): UnseenConfirmKey | null {
  if (!hasRelevantUnseen(unseen))
    return null;

  if (unseen.hasUnseenDocuments && unseen.hasUnseenEmails)
    return "documents_and_email";

  if (unseen.hasUnseenDocuments)
    return "documents_only";

  return "email_only";
}

export function getUnseenConfirmMessage(
  key: UnseenConfirmKey,
  outputEmailCount: number,
): string {
  const multipleEmails = outputEmailCount > 1;
  switch (key) {
    case "documents_and_email":
      return multipleEmails
        ? "You haven't reviewed all documents and your emails. Would you like to proceed?"
        : "You haven't reviewed all documents and your email. Would you like to proceed?";
    case "documents_only":
      return "You haven't reviewed all documents. Would you like to proceed?";
    case "email_only":
      return multipleEmails
        ? "Please review your emails. Would you like to proceed?"
        : "Please review your email. Would you like to proceed?";
  }
}

export function getUnseenFooterWarning(
  key: UnseenConfirmKey,
  outputEmailCount: number,
): string {
  const multipleEmails = outputEmailCount > 1;
  switch (key) {
    case "documents_and_email":
      return multipleEmails
        ? "You haven't reviewed all documents and your emails."
        : "You haven't reviewed all documents and your email.";
    case "documents_only":
      return "You haven't reviewed all documents.";
    case "email_only":
      return multipleEmails
        ? "Please review your emails."
        : "Please review your email.";
  }
}

export function getSuccessTitleKey(ctaKey: PrimaryCtaKey, documentCount = 1): string {
  switch (ctaKey) {
    case "send_email":
      return "Email sent";
    case "approve_and_send":
      return "Document approved";
    case "approve_all":
      return "Documents approved";
    case "approve_documents":
      return documentCount > 1 ? "Documents approved" : "Document approved";
    case "approve_document":
      return "Document approved";
  }
}

export function getSuccessBodyKey(ctaKey: PrimaryCtaKey, hasSignatureRequests = false): string {
  if (hasSignatureRequests) {
    switch (ctaKey) {
      case "send_email":
      case "approve_and_send":
        return "Your approval has been recorded and the email has been queued for delivery. Signature requests will begin shortly.";
      case "approve_all":
      case "approve_documents":
      case "approve_document":
        return "Your approval has been recorded. Signature requests will begin shortly.";
    }
  }

  switch (ctaKey) {
    case "send_email":
    case "approve_and_send":
      return "Your approval has been recorded and the email has been queued for delivery.";
    case "approve_all":
    case "approve_documents":
    case "approve_document":
      return "Your approval has been recorded.";
  }
}
